import { db } from '../firebase';
import {
  collection, getDocs, getDoc,
  doc, query, orderBy, serverTimestamp, getCountFromServer
} from 'firebase/firestore';
import { logService } from './logService';
import { maskIfViewer } from '../utils/permissions';
import { addDoc, updateDoc, deleteDoc } from './guardedDb';

const CUSTOMER_COLLECTION = 'E2B_CUSTOMERS';

export const customerService = {
  // 1. 고객 목록 가져오기 (ID 충돌 방지 로직 적용)
  getCustomers: async () => {
    try {
      const q = query(collection(db, CUSTOMER_COLLECTION), orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      // 데이터 내부의 id:null이 진짜 ID를 덮어쓰지 않도록 순서 조정
      return querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id, name: maskIfViewer(doc.data().name) }));
    } catch (e) {
      console.error("고객 로드 실패:", e);
      return [];
    }
  },

  // 고객 '수'만 필요할 때(모바일 대시보드 등) — 문서를 다 받지 않고 서버 집계로 개수만 가져온다.
  countCustomers: async () => {
    try {
      const snap = await getCountFromServer(collection(db, CUSTOMER_COLLECTION));
      return snap.data().count;
    } catch (e) {
      console.error("고객 수 조회 실패:", e);
      return 0;
    }
  },

  // 2. [중요] 중복 체크 로직 (차량번호 및 차대번호 비교)
  checkDuplicate: (list, formData) => {
    if (!list) return null;
    
    // 🚀 [버그 픽스] 차량번호가 '빈칸'이 아닐 때만 중복 검사! (빈칸끼리 중복 에러 나는 현상 방지)
    if (formData.carNumber && formData.carNumber.trim() !== '') {
      const carNumDup = list.find(c => c.carNumber === formData.carNumber && c.id !== formData.id);
      if (carNumDup) return `❌ 차량번호 [${formData.carNumber}]는 이미 '${carNumDup.name}' 고객님으로 등록되어 있습니다.`;
    }
    
    // 🚀 [버그 픽스] 차대번호가 '빈칸'이 아닐 때만 중복 검사!
    if (formData.vin && formData.vin.trim() !== '') {
      const vinDup = list.find(c => c.vin === formData.vin && c.id !== formData.id);
      if (vinDup) return `❌ 차대번호 [${formData.vin}]는 이미 다른 고객님으로 등록되어 있습니다.`;
    }
    
    return null;
  },

  // 3. 기존 saveCustomer (엑셀 업로드 등 하위 호환성을 위해 유지)
  saveCustomer: async (id, data) => {
    const { id: _, ...pureData } = data; // 저장 시 id 필드를 제외하여 null 값 저장을 방지함
    try {
      if (id) {
        await updateDoc(doc(db, CUSTOMER_COLLECTION, id), { ...pureData, updatedAt: serverTimestamp() });
      } else {
        await addDoc(collection(db, CUSTOMER_COLLECTION), { ...pureData, createdAt: serverTimestamp() });
      }
      logService.record({
        module: '고객관리', action: id ? 'UPDATE' : 'CREATE',
        target: `고객명 [${pureData.name || '이름없음'}]`,
        details: `${id ? '고객 정보 수정' : '신규 고객 등록'} (차량번호: ${pureData.carNumber || '-'})`
      });
    } catch (e) { throw e; }
  },

  // 🚀 4. [핵심 추가] Customer.js와 완벽하게 대화할 수 있도록 addCustomer 기능 추가!
  addCustomer: async (data) => {
    const { id: _, ...pureData } = data;
    try {
      await addDoc(collection(db, CUSTOMER_COLLECTION), { ...pureData, createdAt: serverTimestamp() });
      logService.record({
        module: '고객관리', action: 'CREATE',
        target: `고객명 [${pureData.name || '이름없음'}]`,
        details: `신규 고객 등록 (차량번호: ${pureData.carNumber || '-'}, 연락처: ${pureData.phone || '-'})`
      });
    } catch (e) { throw e; }
  },

  // 🚀 5. [핵심 추가] Customer.js와 완벽하게 대화할 수 있도록 updateCustomer 기능 추가!
  updateCustomer: async (data) => {
    const { id, ...pureData } = data;
    if (!id) throw new Error("수정할 고객 ID가 없습니다.");
    try {
      const docRef = doc(db, CUSTOMER_COLLECTION, id);
      await updateDoc(docRef, { ...pureData, updatedAt: serverTimestamp() });
      logService.record({
        module: '고객관리', action: 'UPDATE',
        target: `고객명 [${pureData.name || id}]`,
        details: `고객 정보 수정 (차량번호: ${pureData.carNumber || '-'}, 연락처: ${pureData.phone || '-'})`
      });
    } catch (e) { throw e; }
  },

  // 6. 고객 삭제
  deleteCustomer: async (id) => {
    try {
      if (!id) return;
      // 무엇이 지워졌는지 남기기 위해 삭제 전에 이름을 확보한다(감사 목적).
      let label = id;
      try {
        const snap = await getDoc(doc(db, CUSTOMER_COLLECTION, id));
        if (snap.exists()) label = snap.data().name || id;
      } catch (_) { /* 이름 확보 실패해도 삭제는 진행 */ }

      await deleteDoc(doc(db, CUSTOMER_COLLECTION, id));
      logService.record({
        module: '고객관리', action: 'DELETE',
        target: `고객명 [${label}]`,
        details: '고객 정보 삭제'
      });
    } catch (e) { throw e; }
  }
};